import { SelfUserDetailsType } from '@/api/v1/users/getSelfUserDetailsApi';
import {
  BILLING_FREQUENCY_DB,
  BILLING_PLAN_SCHEDULE_TYPE,
  BILLING_SUBSCRIPTION_STATUS,
  BillingFrequencyType,
  BillingPlanScheduleType,
  BillingSubscriptionStatusType,
  INFINITY_SIGN,
  SUBSCRIPTION_PLAN_TYPE,
  UNLIMITED_USERS_LIMIT,
} from '@/const/shared';
import { InternalOrganizationType } from '@/bundle/_MasterAdmin/types/types';
import { BillingSubscriptionType } from '@/types/sharedTypes';

// We allow to invite only one user for organization with no subscription
export const DEFAULT_ORGANIZATION_USER_LIMIT = 1;

export const isSingleUsePaymentPlan = (scheduleType: BillingPlanScheduleType) => {
  return scheduleType === BILLING_PLAN_SCHEDULE_TYPE.ONE_TIME;
};

export const isStandardPlanType = (user: SelfUserDetailsType) => {
  if (!user) return;

  return user.organization_user?.organization?.subscription_plan_type === SUBSCRIPTION_PLAN_TYPE.STANDARD;
};

export const isEnterprisePlanType = (user: SelfUserDetailsType) => {
  if (!user) return;

  return user.organization_user?.organization?.subscription_plan_type === SUBSCRIPTION_PLAN_TYPE.ENTERPRISE;
};

export const getBillingDetails = (user: SelfUserDetailsType) => {
  return user?.organization_user?.organization?.billing_profile;
};

export const getBillingCurrentPlan = (user: SelfUserDetailsType) => {
  return getBillingDetails(user)?.current_plan;
};

export const getUsersMaxLimit = (user: SelfUserDetailsType) => {
  return getBillingCurrentPlan(user)?.max_users;
};

export const getBillingSubscription = (user: SelfUserDetailsType) => {
  return getBillingDetails(user)?.subscription;
};

export const hasBillingSubscription = (user: SelfUserDetailsType) => {
  return !!getBillingSubscription(user);
};

export const isTrialMode = (user: SelfUserDetailsType) => {
  return isStandardPlanType(user) && !hasBillingSubscription(user);
};

export const getBillingSubscriptionStatus = (user: SelfUserDetailsType) => {
  return getBillingSubscription(user)?.status;
};

export const isUsersLimitReached = (usersCount: number, maxLimit: number) => {
  return maxLimit !== UNLIMITED_USERS_LIMIT && usersCount >= maxLimit;
};

export const isPaymentsLimitReached = (user: SelfUserDetailsType) => {
  const billingDetails = getBillingDetails(user);

  return billingDetails?.monthly_views_available === 0 && billingDetails?.extra_views_available === 0;
};

export const isActiveSubscriptionStatus = (status: BillingSubscriptionStatusType) => {
  return status === BILLING_SUBSCRIPTION_STATUS.ACTIVE;
};

export const isGraceSubscriptionStatus = (status: BillingSubscriptionStatusType) => {
  return status === BILLING_SUBSCRIPTION_STATUS.GRACE;
};

export const isSuspendedSubscriptionStatus = (status: BillingSubscriptionStatusType) => {
  return status === BILLING_SUBSCRIPTION_STATUS.SUSPENDED;
};

export const isCancelledSubscriptionStatus = (status: BillingSubscriptionStatusType) => {
  return status === BILLING_SUBSCRIPTION_STATUS.CANCELLED;
};

export const isPendingCancellationSubscriptionStatus = (subscription: BillingSubscriptionType) => {
  if (!subscription) return;

  const isCancelledSubscription = isCancelledSubscriptionStatus(subscription.status);

  return !!subscription.cancel_at_period_end && !isCancelledSubscription;
};

export const isYearlyFrequency = (frequency: BillingFrequencyType) => {
  return frequency === BILLING_FREQUENCY_DB.YEAR;
};

export const isMonthlyFrequency = (frequency: BillingFrequencyType) => {
  return frequency === BILLING_FREQUENCY_DB.MONTH;
};

export const getUsersLimit = (
  organizationDetails: InternalOrganizationType,
  usersLimit: number = DEFAULT_ORGANIZATION_USER_LIMIT
) => {
  if (!organizationDetails) return '';

  const { subscription_plan_type, active_users_count } = organizationDetails;

  if (subscription_plan_type === SUBSCRIPTION_PLAN_TYPE.ENTERPRISE || usersLimit === UNLIMITED_USERS_LIMIT) {
    return `${active_users_count}/${INFINITY_SIGN}`;
  }

  return `${active_users_count}/${usersLimit}`;
};
